import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/_ui/Select'
import T, { useTranslate } from '@/components/Helpers/Translate'
import type { CustomerType } from '@/store/types/user'
import useGlobalStore from '@/store/useGlobalStore'
import { useRouter } from 'next/router'
import React from 'react'

export type ProductSortByOptionsType =
  | 'nameAZ'
  | 'nameZA'
  | 'priceLowToHigh'
  | 'priceHighToLow'
  | 'pvLowToHigh'
  | 'pvHighToLow'

export const productsSortBySortingOptions: {
  [key: string]: {
    value: ProductSortByOptionsType
    availableFor?: CustomerType[]
    sortBy?: 'name' | 'price' | 'pv'
    orderBy?: 'asc' | 'desc'
  }
} = {
  nameAZ: {
    value: 'nameAZ',
    sortBy: 'name',
    orderBy: 'asc',
  },
  nameZA: {
    value: 'nameZA',
    sortBy: 'name',
    orderBy: 'desc',
  },
  priceLowToHigh: {
    value: 'priceLowToHigh',
    sortBy: 'price',
    orderBy: 'asc',
  },
  priceHighToLow: {
    value: 'priceHighToLow',
    sortBy: 'price',
    orderBy: 'desc',
  },
  pvLowToHigh: {
    value: 'pvLowToHigh',
    sortBy: 'pv',
    orderBy: 'asc',
    availableFor: ['Associate'],
  },
  pvHighToLow: {
    value: 'pvHighToLow',
    sortBy: 'pv',
    orderBy: 'desc',
    availableFor: ['Associate'],
  },
}

export const ProductsSortBy = () => {
  const {
    user: { customerType },
  } = useGlobalStore()
  const { translate } = useTranslate()

  const router = useRouter()

  const urlParams = new URLSearchParams(window.location.search)

  const sortByUrlQueryParam = urlParams.get('sortBy')
  const orderByUrlQueryParam = urlParams.get('orderBy')

  const matchedProperty = Object.entries(productsSortBySortingOptions).find(
    ([_, option]) =>
      option.sortBy === sortByUrlQueryParam &&
      option.orderBy === orderByUrlQueryParam,
  )?.[1]

  const sortBySelectValue = matchedProperty?.value ?? 'nameAZ'
  const [isBlockingClick, setIsBlockingClick] = React.useState(false)

  const onValueChange = async (value: string) => {
    setIsBlockingClick(true)

    await router.push(
      {
        pathname: router.pathname,
        query: {
          ...router.query,
          sortBy: productsSortBySortingOptions[value].sortBy,
          orderBy: productsSortBySortingOptions[value].orderBy,
        },
      },
      undefined,
      { shallow: true },
    )
    setTimeout(() => setIsBlockingClick(false), 300)
  }

  return (
    <>
      {isBlockingClick && (
        <div className="fixed inset-0 z-[9999] bg-transparent" />
      )}
      <Select value={sortBySelectValue} onValueChange={onValueChange}>
        <SelectTrigger className="w-auto border-none bg-[#FAFAFA] text-primary focus:ring-secondary ">
          <p className="mr-6 line-clamp-1">
            <T>sort_by</T>:
          </p>
          <SelectValue placeholder={translate('sort_by')} />
        </SelectTrigger>
        <SelectContent className="text-primary">
          <SelectGroup>
            {Object.values(productsSortBySortingOptions).map((option) =>
              !option.availableFor ||
              option.availableFor.includes(customerType) ? (
                <SelectItem key={option.value} value={option.value}>
                  <T className="font-semibold">{option.value}</T>
                </SelectItem>
              ) : null,
            )}
          </SelectGroup>
        </SelectContent>
      </Select>
    </>
  )
}
